import { Box, Typography, useTheme, Paper } from "@mui/material";
import { TableHeaderContainer } from "../components/container/TableHeaderContainer";
import { TableTitleContainer } from "../components/container/TableTitleContainer";
import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { FiBriefcase, FiCheckSquare, FiClipboard, FiFile, FiGrid, FiInfo, FiTarget, FiUser } from "react-icons/fi";
import ResizableTextBox from "../components/input/ResizableTextBox";
import TextBox from "../components/input/TextBox";
import { useContextProvider } from "../components/page-context/ContextProvider";
import FormSelect from "../components/select/FormSelect";
import { Building } from "../db_access/building";
import { Product } from "../db_access/product";
import { Project } from "../db_access/project";
import { LifeCycleAssessment as LCA } from "../db_access/life_cycle_assessment";
import { getOrgTokens } from "../theme/style/organizationPalette";

const ORG_OPTIONS = [
  { key: "equity", label: "Equity share" },
  { key: "financial", label: "Financial control" },
  { key: "operational", label: "Operational control" },
];

const DOMAIN_ROLES = {
  "Manufacturing": ["Production Manager", "Manufacturing Engineer", "Process Engineer", "Product Designer", "Industrial Designer", "Eco-design Specialist", "Circular Economy Designer", "R&D Engineer", "Plant Manager", "Quality Engineer", "Sustainability Manager", "Product Stewardship Manager", "Environmental Manager"],
  "Supply Chain & Logistics": ["Supply Chain Manager", "Procurement Manager", "Purchasing Manager", "Vendor Manager", "Supplier Representative", "Logistics Manager", "Warehouse Manager", "Inventory Manager", "Distribution Manager", "Export Manager", "Import Manager"],
  "Design & Engineering": ["Architect", "Landscape Architect", "Interior Designer", "Civil Engineer", "Structural Engineer", "Mechanical Engineer", "Electrical Engineer", "Environmental Engineer", "Manufacturing Engineer", "Process Engineer", "Design Engineer", "BIM Engineer", "Design Consultant"],
  "Construction & Infrastructure": ["Project Manager", "Construction Manager", "Site Engineer", "Builder", "Main Contractor", "General Contractor", "EPC Engineer", "Quantity Surveyor", "Project Engineer", "Asset Manager", "Facility Manager", "Building Manager"],
  "Operations & Maintenance": ["Operations Manager", "Maintenance Manager", "Maintenance Engineer", "Reliability Engineer", "Asset Manager", "Facility Manager", "Utility Manager", "Plant Manager", "Service Engineer"],
  "Circular Economy & End-of-Life": ["Circular Economy Manager", "Recycling Manager", "Resource Recovery Manager", "Waste Manager", "Recycling Engineer", "Remanufacturing Engineer", "Refurbishment Manager", "Repair Engineer", "Reverse Logistics Manager", "Take-back Program Manager", "Material Recovery Specialist"],
  "Sustainability & Assessment": ["Sustainability Manager", "ESG Manager", "Environmental Manager", "Circular Economy Consultant", "Environmental Consultant", "Sustainability Consultant", "LCA Practitioner", "LCA Consultant", "Carbon Consultant", "Net Zero Consultant", "Product Stewardship Manager"],
  "Compliance & Verification": ["Compliance Officer", "Regulatory Affairs Manager", "Auditor", "Third-party Verifier", "Certification Auditor", "EPD Specialist", "Quality Assurance Manager", "Quality Control Engineer", "Environmental Compliance Officer", "Government Inspector"],
  "Digital Product Management": ["Product Manager", "Product Data Manager", "Product Lifecycle (PLM) Manager", "BIM Manager", "Digital Product Passport (DPP) Manager", "Material Passport Manager", "Information Manager", "Digital Twin Manager"],
  "Research & Academia": ["Professor", "Research Scientist", "Research Engineer", "University Researcher", "Academic Researcher", "Postdoctoral Researcher", "Doctoral Researcher (PhD)", "Student", "Laboratory Technician"],
};

const DOMAIN_LIST = [...Object.keys(DOMAIN_ROLES), "Other"];

const ScopeAndGoals = ({ pageType, selectedRow }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const tok = getOrgTokens(isDark);
  const { isSaveClicked, setIsSaveVisible, setIsSaveClicked, setIsLoading, setRefresh } = useContextProvider();
  const units = [
    "mg", "g", "kg", "ton", "oz", "lb", "kWh", "J", "kJ", "GJ", "Btu", "mmBTU", "t.km", "t.mi", "t.nmi", "s", "min", "h", "day", "wk", "mo", "yr",
    "mm3", "cm3", "dm3", "m3", "km3", "inch3", "ft3", "L", "gal", "fl_oz", "barrel", "ml", "mm2", "cm2", "dm2", "m2", "km2", "inch2", "ft2", "mm",
    "cm", "dm", "m", "km", "inch", "ft", "yd", "mi", "nmi", "unit", "piece", "other"
  ];

  const [formData, setFormData] = useState({
    name: "",
    functional_unit: "",
    other_unit: "",
    scope: "",
    goals: "",
    service_life: "",
    specifications: "",
    assumptions: "",
    system_boundary: "",
    domain: DOMAIN_LIST[0],
    domainCustom: "",
    role: DOMAIN_ROLES[DOMAIN_LIST[0]][0],
    roleCustom: "",
    org: "operational",
    equity_percent: "",
    epd_link: "",
    pcr_link: "",
    is_reusable: false,
  });

  const [lcaData, setLcaData] = useState({
    lca_software: "",
    lci_database: "",
    lcia_methodology: "",
    cutoff: "",
    allocation_procedure: "",
  });

  const [lcaId, setLcaId] = useState(null);
  const [modified, setModified] = useState(false);

  // Helper to fetch/initialize LCA records
  const getLcaInfo = useCallback((id) => {
    if (!id) return;
    const request = (pageType === "product" || pageType === "supplier")
      ? LCA.get_by_product_id
      : LCA.get_by_project_id;

    request(id)
      .then((res) => res.json())
      .then((data) => {
        if (data.length === 0) {
          const lca = {
            product_id: (pageType === "product" || pageType === "supplier") ? id : null,
            project_id: (pageType === "product" || pageType === "supplier") ? null : id,
            reused_product_id: null,
            reference_pcr: null,
            declared_unit: null,
            scope: null,
            lca_software: null,
            lci_database: null,
            lcia_methodology: null,
            cutoff: null,
            allocation_procedure: null,
            primary_sources: null,
            interpretation: null,
          };
          LCA.new(lca).then(() => getLcaInfo(id));
        } else {
          const item = data[0];
          setLcaId(item.id);
          setLcaData({
            lca_software: item.lca_software || "",
            lci_database: item.lci_database || "",
            lcia_methodology: item.lcia_methodology || "",
            cutoff: item.cutoff || "",
            allocation_procedure: item.allocation_procedure || "",
          });
        }
      })
      .catch(() => {});
  }, [pageType]);

  useEffect(() => {
    if (selectedRow != undefined) {
      const isEpdValue = selectedRow.is_epd === 1;
      setFormData((prev) => ({
        ...prev,
        name: selectedRow.name || "",
        functional_unit: selectedRow.functional_unit || "",
        other_unit: selectedRow.other_unit || "",
        scope: selectedRow.scope || "",
        goals: selectedRow.goals || "",
        assumptions: selectedRow.assumptions || "",
        system_boundary: selectedRow.system_boundary || "",
        epd_link: selectedRow.epd_link || "",
        pcr_link: selectedRow.pcr_link || "",
        isEpd: isEpdValue,
      }));
      getLcaInfo(selectedRow.id);
    }
    setModified(false);
  }, [selectedRow, getLcaInfo]);

  const handleChange = (field) => (e) => {
    if (formData.isEpd) return;
    const value = field === "isReuseScenario" || field === "isEpd" ? e.target.checked : e.target.value;
    setFormData({ ...formData, [field]: value });
    setIsSaveVisible(true);
    setModified(true);
  };

  const handleLcaChange = (field) => (e) => {
    if (formData.isEpd) return;
    setLcaData((prev) => ({ ...prev, [field]: e.target.value }));
    setIsSaveVisible(true);
    setModified(true);
  };

  const rolesForDomain = formData.domain !== "Other" ? (DOMAIN_ROLES[formData.domain] || []) : [];

  const handleDomainChange = (d) => {
    if (formData.isEpd) return;
    setFormData({
      ...formData,
      domain: d,
      role: d === "Other" ? "" : (DOMAIN_ROLES[d]?.[0] ?? "Other"),
      roleCustom: "",
      domainCustom: d !== "Other" ? "" : formData.domainCustom,
    });
    setIsSaveVisible(true);
    setModified(true);
  };

  const handleRoleChange = (r) => {
    if (formData.isEpd) return;
    setFormData({ ...formData, role: r, roleCustom: "" });
    setIsSaveVisible(true);
    setModified(true);
  };

  useEffect(() => {
    if (isSaveClicked && modified) {
      const fd = new FormData();
      Object.keys(formData).forEach((k) => {
        fd.append(k, formData[k]);
      });
      fd.append("id", selectedRow.id);

      let request;
      if (pageType === "building") {
        request = Building.save_scope_and_goals(fd);
      } else if (pageType === "product" || pageType === "supplier") {
        request = Product.save_scope_and_goals(fd);
      } else {
        request = Project.save_scope_and_goals(fd);
      }

      request
        .then((res) => {
          if (!res.ok) throw new Error("Failed to update");
          return res.json();
        })
        .then(() => {
          if (lcaId) {
            const lcaPayload = {
              id: lcaId,
              product_id: (pageType === "product" || pageType === "supplier") ? selectedRow.id : null,
              project_id: (pageType === "product" || pageType === "supplier") ? null : selectedRow.id,
              lca_software: lcaData.lca_software,
              lci_database: lcaData.lci_database,
              lcia_methodology: lcaData.lcia_methodology,
              cutoff: lcaData.cutoff,
              allocation_procedure: lcaData.allocation_procedure,
            };
            const updateLca = (pageType === "product" || pageType === "supplier")
              ? LCA.update_lca_by_product(lcaPayload)
              : LCA.update_lca_by_project(lcaPayload);
            return updateLca;
          }
        })
        .catch((err) => {
          console.error("Error saving scope/goals/lca:", err);
        })
        .finally((_) => {
          setModified(false);
          setIsSaveClicked(false);
          setIsLoading(false);
          setRefresh((r) => !r);
        });
    }
  }, [isSaveClicked, modified, formData, lcaData, lcaId, pageType, selectedRow?.id, setIsSaveClicked, setIsLoading, setRefresh]);

  return (
    <Paper variant="Table Container">
      <TableHeaderContainer>
        <TableTitleContainer>
          <Typography variant="Table Title">{t("Scope and Goals")}</Typography>
        </TableTitleContainer>
      </TableHeaderContainer>
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <Box sx={{ display: "flex", gap: 3 }}>
            <Box style={{ flex: 1 }}>
              <TextBox Icon={FiFile} label={"Name"} placeholder={"Name"} value={formData.name} onChange={handleChange("name")} disabled={formData.isEpd} />
            </Box>
            <Box style={{ flex: 1 }}>
              <FormSelect
                Icon={FiClipboard}
                label={"Functional Unit"}
                placeholder={"Functional Unit"}
                options={units}
                value={formData.functional_unit}
                onChange={handleChange("functional_unit")}
                disabled={formData.isEpd}
              />
            </Box>
            {formData.functional_unit === "other" && (
              <Box style={{ flex: 1 }}>
                <TextBox Icon={FiFile} label={"Custom Unit"} placeholder={"Specify other functional unit"} value={formData.other_unit} onChange={handleChange("other_unit")} disabled={formData.isEpd} />
              </Box>
            )}
          </Box>
          <Box sx={{ display: "flex", gap: 3 }}>
            <Box style={{ flex: 1 }}>
              <ResizableTextBox Icon={FiTarget} label={"Scope"} placeholder={"Scope"} value={formData.scope} onChange={handleChange("scope")} disabled={formData.isEpd} />
            </Box>
            <Box style={{ flex: 1 }}>
              <ResizableTextBox Icon={FiCheckSquare} label={"Goals"} placeholder={"Goals"} value={formData.goals} onChange={handleChange("goals")} disabled={formData.isEpd} />
            </Box>
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Box style={{ flex: 1 }}>
              <ResizableTextBox
                Icon={FiInfo}
                label={"Assumptions"}
                placeholder={"Assumptions"}
                value={formData.assumptions}
                onChange={handleChange("assumptions")}
                disabled={formData.isEpd}
              />
            </Box>
          </Box>

          {/* LCA Parameters Section (Moved above System Boundary) */}
          <Box sx={{ display: "flex", gap: 3 }}>
            <Box style={{ flex: 1 }}>
              <TextBox
                Icon={FiFile}
                label={"LCA Software"}
                placeholder={"LCA Software"}
                value={lcaData.lca_software}
                onChange={handleLcaChange("lca_software")}
                disabled={formData.isEpd}
              />
            </Box>
            <Box style={{ flex: 1 }}>
              <TextBox
                Icon={FiFile}
                label={"LCI Database(s)"}
                placeholder={"LCI Database(s)"}
                value={lcaData.lci_database}
                onChange={handleLcaChange("lci_database")}
                disabled={formData.isEpd}
              />
            </Box>
            <Box style={{ flex: 1 }}>
              <TextBox
                Icon={FiFile}
                label={"LCIA Methodology"}
                placeholder={"LCIA Methodology"}
                value={lcaData.lcia_methodology}
                onChange={handleLcaChange("lcia_methodology")}
                disabled={formData.isEpd}
              />
            </Box>
          </Box>
          <Box sx={{ display: "flex", gap: 3 }}>
            <Box style={{ flex: 1 }}>
              <ResizableTextBox
                Icon={FiClipboard}
                label={"Cut-Off"}
                placeholder={"Cut-Off criteria"}
                value={lcaData.cutoff}
                onChange={handleLcaChange("cutoff")}
                disabled={formData.isEpd}
              />
            </Box>
            <Box style={{ flex: 1 }}>
              <ResizableTextBox
                Icon={FiClipboard}
                label={"Allocation Procedure"}
                placeholder={"Allocation procedure"}
                value={lcaData.allocation_procedure}
                onChange={handleLcaChange("allocation_procedure")}
                disabled={formData.isEpd}
              />
            </Box>
          </Box>

          <Box sx={{ display: "flex", gap: 1 }}>
            <Box style={{ flex: 1 }}>
              <ResizableTextBox
                Icon={FiTarget}
                label={"System Boundary"}
                placeholder={"System Boundary"}
                value={formData.system_boundary}
                onChange={handleChange("system_boundary")}
                disabled={formData.isEpd}
              />
            </Box>
          </Box>
          <Box sx={{ display: "flex", gap: 3 }}>
            <Box style={{ flex: 1 }}>
              <FormSelect
                Icon={FiGrid}
                label={"Domain"}
                placeholder={"Domain"}
                options={DOMAIN_LIST}
                value={formData.domain}
                onChange={(e) => handleDomainChange(e.target.value)}
                disabled={formData.isEpd}
              />
              {formData.domain === "Other" && (
                <Box sx={{ mt: 1.5 }}>
                  <TextBox
                    Icon={FiGrid}
                    label={"Domain (specify)"}
                    placeholder={"Describe your domain…"}
                    value={formData.domainCustom}
                    onChange={handleChange("domainCustom")}
                    disabled={formData.isEpd}
                  />
                </Box>
              )}
            </Box>
            {formData.domain !== "Other" && (
              <Box style={{ flex: 1 }}>
                <FormSelect
                  Icon={FiUser}
                  label={"User Type"}
                  placeholder={"User Type"}
                  options={[...rolesForDomain, { value: "Other", label: "Other…" }]}
                  value={formData.role}
                  onChange={(e) => handleRoleChange(e.target.value)}
                  disabled={formData.isEpd}
                />
                {formData.role === "Other" && (
                  <Box sx={{ mt: 1.5 }}>
                    <TextBox
                      Icon={FiUser}
                      label={"User Type (specify)"}
                      placeholder={"Type your role…"}
                      value={formData.roleCustom}
                      onChange={handleChange("roleCustom")}
                      disabled={formData.isEpd}
                    />
                  </Box>
                )}
              </Box>
            )}
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Box style={{ flex: 1 }}>
              <FormSelect
                Icon={FiBriefcase}
                label={"Organizational Boundary"}
                placeholder={"Organizational Boundary"}
                options={ORG_OPTIONS.map((o) => ({ value: o.key, label: o.label }))}
                value={formData.org}
                onChange={handleChange("org")}
                disabled={formData.isEpd}
              />
              {formData.org === "equity" && (
                <Box sx={{ mt: 1.5 }}>
                  <TextBox
                    Icon={FiBriefcase}
                    type="number"
                    label={"Equity Share (%)"}
                    placeholder={"Equity Share (%)"}
                    value={formData.equity_percent}
                    onChange={handleChange("equity_percent")}
                    disabled={formData.isEpd}
                  />
                </Box>
              )}
            </Box>
          </Box>
          {pageType === "product" && (
            <Box sx={{ display: "flex", gap: 1 }}>
              <Box style={{ flex: 1 }}>
                <TextBox Icon={FiFile} label={"EPD Link"} placeholder={"EPD Link"} value={formData.epd_link} onChange={handleChange("epd_link")} disabled={formData.isEpd} />
              </Box>
              <Box style={{ flex: 1 }}>
                <TextBox
                  Icon={FiFile}
                  label={"PCR Document Link"}
                  placeholder={"PCR Document Link"}
                  value={formData.pcr_link}
                  onChange={handleChange("pcr_link")}
                  disabled={formData.isEpd}
                />
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </Paper>
  );
};

export default ScopeAndGoals;
